/**
 * Pure helpers behind the page <head>: titles, descriptions, canonical URLs,
 * share images and JSON-LD. No Nuxt imports, so they are unit-tested directly
 * (tests/seo.test.ts) and can be shared with server routes.
 */

/**
 * The one public host. app. and ohc.architecture.org 301 here, so canonical and
 * og:url always name this host, whichever hostname rendered the page (staging
 * included, which keeps a staging page from ever presenting itself as the
 * original).
 */
export const SEO_ORIGIN = 'https://openhousechicago.org';

export const SEO_SITE_NAME = 'Open House Chicago';

/** Generic share card: the 2026 artwork letterboxed to the 1.91:1 link-preview shape. */
export const SEO_DEFAULT_IMAGE = {
    url: `${SEO_ORIGIN}/img/ohc-share-2026-landscape.jpg`,
    width: 1200,
    height: 630,
    alt: 'Open House Chicago, October 17–18, 2026. Chicago Architecture Center.',
};

export const SEO_DESCRIPTION_LENGTH = 155;

const NAMED_ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    rsquo: '’',
    lsquo: '‘',
    rdquo: '”',
    ldquo: '“',
    ndash: '–',
    mdash: '—',
    hellip: '…',
    eacute: 'é',
    copy: '©',
    reg: '®',
};

export function decodeEntities(text: string): string {
    return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
        if (entity[0] === '#') {
            const code = entity[1] === 'x' || entity[1] === 'X' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
            return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
        }
        return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
    });
}

/** HTML or Airtable rich text to one line of plain text. */
export function toPlainText(input: unknown): string {
    if (typeof input !== 'string' || !input) return '';
    const withoutTags = input
        // Block boundaries and line breaks become spaces, so "</p><p>" never
        // glues the last word of one paragraph to the first of the next.
        .replace(/<\s*(br|\/p|\/div|\/li|\/h[1-6])\b[^>]*>/gi, ' ')
        .replace(/<[^>]*>/g, '');
    return decodeEntities(withoutTags)
        .replace(/[\s ]+/g, ' ')
        .trim();
}

/**
 * A meta description: plain text, cut at a word boundary to at most `max`
 * characters (ellipsis included), so no snippet ends mid-word or mid-tag.
 */
export function seoDescription(input: unknown, max = SEO_DESCRIPTION_LENGTH): string {
    const text = toPlainText(input);
    if (text.length <= max) return text;
    const room = text.slice(0, max - 1);
    const lastSpace = room.lastIndexOf(' ');
    // A single unbroken run longer than the budget is cut where it is rather
    // than returned whole.
    const cut = lastSpace > max * 0.5 ? room.slice(0, lastSpace) : room;
    return cut.replace(/[\s,;:.\-–—(]+$/, '') + '…';
}

/** "<Name> | Open House Chicago". /api/seo splits titles on "|" and keeps the left side. */
export function detailTitle(name: unknown): string {
    const clean = toPlainText(name);
    return clean ? `${clean} | ${SEO_SITE_NAME}` : SEO_SITE_NAME;
}

/**
 * Absolute canonical URL for a route path: production host, no query or hash,
 * and no trailing slash except on the root — the form every in-app link uses
 * (/sites/rec…, /events, /neighborhoods/rec…).
 */
export function canonicalUrl(path: unknown): string {
    let clean = typeof path === 'string' ? path.split(/[?#]/)[0] : '/';
    if (!clean.startsWith('/')) clean = '/' + clean;
    clean = clean.replace(/\/{2,}/g, '/');
    if (clean.length > 1) clean = clean.replace(/\/+$/, '');
    return SEO_ORIGIN + clean;
}

const OG_WIDTH = 1200;
const OG_HEIGHT = 630;

/** Best source URL for an Airtable attachment: R2 copy first, as useImageUrl does. */
function attachmentUrl(image: any): string {
    if (!image) return '';
    return image.r2Url || image.thumbnails?.large?.url || image.url || '';
}

export interface ShareImage {
    url: string;
    width: number;
    height: number;
    alt: string;
}

/**
 * A 1200x630 JPEG share image for a record's photo, through the same Cloudflare
 * resizer (/cdn-cgi/image/) the page's <nuxt-img> tags use. JPEG rather than
 * f=auto: link-preview crawlers are not reliably able to read AVIF. Falls back
 * to the generic card when the record has no usable photo.
 *
 * `index` defaults to the LAST attachment, which is the one the cards and the
 * hero carousel lead with (the lists are reversed for display).
 */
export function shareImage(images: unknown, alt: string, index?: number): ShareImage {
    const list = Array.isArray(images) ? images : [];
    const image = list.length ? list[index ?? list.length - 1] : null;
    const src = attachmentUrl(image);
    if (!/^https?:\/\//.test(src)) return { ...SEO_DEFAULT_IMAGE };
    return {
        url: `${SEO_ORIGIN}/cdn-cgi/image/w=${OG_WIDTH},h=${OG_HEIGHT},fit=cover,f=jpeg,q=85/${src}`,
        width: OG_WIDTH,
        height: OG_HEIGHT,
        alt: toPlainText(alt) || SEO_DEFAULT_IMAGE.alt,
    };
}

export interface PageSeo {
    title: string;
    description: string;
    /** Pages the crawler should not index (account and utility screens). */
    noindex?: boolean;
}

const DEFAULT_PAGE_SEO: PageSeo = {
    title: 'Open House Chicago | Chicago Architecture Center',
    description:
        "Open House Chicago is the Chicago Architecture Center's free festival of architecture, October 17–18, 2026. Go inside buildings across the city's neighborhoods.",
};

/** Per-path titles and descriptions for the list and landing pages. */
const PAGE_SEO: Record<string, PageSeo> = {
    '/': DEFAULT_PAGE_SEO,
    '/sites': {
        title: `2026 Sites | ${SEO_SITE_NAME}`,
        description:
            'Browse every building open for Open House Chicago 2026, October 17–18. Filter by neighborhood and day, then save sites to plan your weekend.',
    },
    '/events': {
        title: `Events | ${SEO_SITE_NAME}`,
        description:
            'Talks, performances, demonstrations and family activities at Open House Chicago sites on October 17–18, 2026. Filter by day, time and neighborhood.',
    },
    '/neighborhoods': {
        title: `Neighborhood Areas | ${SEO_SITE_NAME}`,
        description:
            'Explore Open House Chicago 2026 by neighborhood area and find the sites, itineraries and events in each part of the city.',
    },
    '/itineraries': {
        title: `Itineraries | ${SEO_SITE_NAME}`,
        description:
            'Themed routes through Open House Chicago 2026 sites, put together by the Chicago Architecture Center to help you plan your weekend.',
    },
    '/about': {
        title: `About the Chicago Architecture Center | ${SEO_SITE_NAME}`,
        description:
            'The Chicago Architecture Center is a nonprofit dedicated to inspiring people to discover why design matters. It presents Open House Chicago every October.',
    },
    '/about-cac': {
        title: `About the Chicago Architecture Center | ${SEO_SITE_NAME}`,
        description:
            'The Chicago Architecture Center is a nonprofit dedicated to inspiring people to discover why design matters. It presents Open House Chicago every October.',
    },
    '/about-ohc': {
        title: `About Open House Chicago | ${SEO_SITE_NAME}`,
        description:
            'How Open House Chicago works: a free weekend, October 17–18, 2026, when buildings across Chicago open their doors to the public.',
    },
    '/archive': {
        title: `Archive | ${SEO_SITE_NAME}`,
        description: 'Sites from past years of Open House Chicago, the Chicago Architecture Center’s annual festival of architecture.',
    },
    '/my-ohc': { title: `My OHC | ${SEO_SITE_NAME}`, description: DEFAULT_PAGE_SEO.description, noindex: true },
    '/login': { title: `Log In | ${SEO_SITE_NAME}`, description: DEFAULT_PAGE_SEO.description, noindex: true },
    '/ohc-home': { ...DEFAULT_PAGE_SEO, noindex: true },
    '/offline': { title: `Offline | ${SEO_SITE_NAME}`, description: DEFAULT_PAGE_SEO.description, noindex: true },
};

/** Title, description and indexing for a path; unknown paths get the festival defaults. */
export function pageSeo(path: unknown): PageSeo {
    const clean = canonicalUrl(path).slice(SEO_ORIGIN.length);
    if (PAGE_SEO[clean]) return PAGE_SEO[clean];
    if (clean.startsWith('/my-ohc/')) return PAGE_SEO['/my-ohc'];
    return DEFAULT_PAGE_SEO;
}

const ORGANIZER = {
    '@type': 'Organization',
    name: 'Chicago Architecture Center',
    url: 'https://architecture.org',
};

/** The festival itself, for the home page. */
export function festivalJsonLd() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: 'Open House Chicago 2026',
        description: DEFAULT_PAGE_SEO.description,
        startDate: '2026-10-17',
        endDate: '2026-10-18',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        isAccessibleForFree: true,
        location: {
            '@type': 'Place',
            name: 'Chicago',
            address: { '@type': 'PostalAddress', addressLocality: 'Chicago', addressRegion: 'IL', addressCountry: 'US' },
        },
        image: [SEO_DEFAULT_IMAGE.url],
        organizer: ORGANIZER,
        url: `${SEO_ORIGIN}/`,
    };
}

/**
 * Airtable stores an event's LOCAL wall-clock time with a `Z` suffix (see
 * composables/eventDay.ts), so the UTC components are the Chicago time.
 * October in Chicago is always Central Daylight Time (DST ends in November).
 */
const CHICAGO_OCTOBER_OFFSET = '-05:00';
const pad = (n: number) => String(n).padStart(2, '0');

function wallClock(date: Date, hours: number, minutes: number): string {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(hours)}:${pad(minutes)}:00${CHICAGO_OCTOBER_OFFSET}`;
}

/** The end of a "10 AM - 5 PM" / "1:00 pm - 2:30 pm" range as [hours, minutes], or null. */
export function parseEndTime(time: unknown): [number, number] | null {
    if (typeof time !== 'string') return null;
    const match = time.trim().match(/[-–—]\s*(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s*m\.?$/i);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    if (hours < 1 || hours > 12 || minutes > 59) return null;
    const pm = match[3].toLowerCase() === 'p';
    if (hours === 12) hours = pm ? 12 : 0;
    else if (pm) hours += 12;
    return [hours, minutes];
}

/**
 * schema.org Event for one OHC programme, from fields that are always present
 * on published events (title, Date) plus whatever else the record reliably
 * carries. Returns null when the record cannot support a valid Event.
 */
export function eventJsonLd(event: any, site: any, opts: { url: string; image?: ShareImage | null }) {
    const fields = event?.fields ?? {};
    const name = toPlainText(fields['Program Title']);
    const date = fields['Date'] ? new Date(fields['Date']) : null;
    if (!name || !date || Number.isNaN(date.getTime())) return null;

    const startDate = wallClock(date, date.getUTCHours(), date.getUTCMinutes());
    const end = parseEndTime(fields['Time']);
    // Only when it follows the start: a few records' Time and Date disagree.
    const endDate = end && end[0] * 60 + end[1] > date.getUTCHours() * 60 + date.getUTCMinutes() ? wallClock(date, end[0], end[1]) : null;

    const siteFields = site?.fields ?? {};
    const streetAddress = toPlainText(siteFields['Site Address'] || fields['Address']);
    const placeName = toPlainText(siteFields['Site'] || siteFields['Name']);

    const ld: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name,
        startDate,
        ...(endDate ? { endDate } : {}),
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: {
            '@type': 'Place',
            ...(placeName ? { name: placeName } : {}),
            address: {
                '@type': 'PostalAddress',
                ...(streetAddress ? { streetAddress } : {}),
                addressLocality: toPlainText(siteFields['City']) || 'Chicago',
                addressRegion: toPlainText(siteFields['State']) || 'IL',
                ...(siteFields['ZIP'] ? { postalCode: String(siteFields['ZIP']) } : {}),
                addressCountry: 'US',
            },
        },
        organizer: ORGANIZER,
        superEvent: { '@type': 'Event', name: 'Open House Chicago 2026', url: `${SEO_ORIGIN}/` },
        url: opts.url,
    };
    const description = seoDescription(fields['Description'], 300);
    if (description) ld.description = description;
    if (opts.image?.url) ld.image = [opts.image.url];
    return ld;
}

/** JSON for a <script type="application/ld+json">, with "<" escaped so no value can close the tag. */
export function serializeJsonLd(data: unknown): string {
    return JSON.stringify(data).replace(/</g, '\\u003c');
}
